import { CaretLeft, CaretRight, Check, Info } from '@phosphor-icons/react'
import { DialogClose } from '@radix-ui/react-dialog'
import { PopoverPortal } from '@radix-ui/react-popover'
import { createScope, splitText, animate, Scope } from 'animejs'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { DesktopCosts, MobileCosts } from '@/blocks/Packets/Costs'
import { LocationPrices } from '@/src/@types/blinkadmin'
import { PacketsBlock } from '@/src/@types/statamic'
import {
  adminDeleteFile,
  adminGetFile,
  adminPostFile,
  get,
} from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogSimple,
  DialogTrigger,
} from '@/src/components/core/Dialog'
import ErrorMessage from '@/src/components/core/ErrorMessage'
import FileUpload from '@/src/components/core/fileUpload/FileUpload'
import ImageUploadMask from '@/src/components/core/fileUpload/masks/ImageUploadMask'
import { Input } from '@/src/components/core/Input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/src/components/core/Popover'
import { Switch } from '@/src/components/core/Switch'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/src/components/core/Tabs'
import { Tag } from '@/src/components/core/Tag'
import useLaravelForm from '@/src/hooks/useLaravelForm'
import useUtmCache from '@/src/hooks/useUTMCache'
import trackEvent from '@/src/lib/tracking'

export async function getStaticProps({ single_location }: PacketsBlock) {
  const location = await get<{ name: string }>(
    `/website/v2/locations/${single_location.value}`
  )

  const locationPrices = await get<LocationPrices>(
    `/website/v2/locations/${single_location.value}/prices`
  )

  if (
    !locationPrices ||
    locationPrices.lessonB45 === 0 ||
    locationPrices.tenPackage === 0 ||
    locationPrices.fiverPackage === 0
  ) {
    return {
      validLocation: false,
    }
  }

  const vku_course = locationPrices.courses?.find(
    (course) => course.type === 'VKU'
  )

  const examPriceRange =
    locationPrices.exams && locationPrices.exams.length > 0
      ? locationPrices.exams?.reduce(
          (range, exam) => {
            if (exam.withPreparation / 100 < range.min) {
              range.min = exam.withPreparation / 100
            }
            if (exam.withPreparation / 100 > range.max) {
              range.max = exam.withPreparation / 100
            }
            return range
          },
          { min: Infinity, max: -Infinity }
        )
      : null

  return {
    validLocation: true,
    locationId: single_location.value,
    locationName: location.name,
    prices: {
      lesson_price: locationPrices.lessonB45 / 100,
      lesson_60_price: locationPrices.lessonB60 / 100,
      lesson_75_price: locationPrices.lessonB75 / 100,
      lesson_90_price: locationPrices.lessonB90 / 100,
      packet_10_price: locationPrices.tenPackage / 100,
      packet_5_price: locationPrices.fiverPackage / 100,
      packet_vku_price: vku_course
        ? locationPrices.tenPackageWithVKU / 100 || null
        : null,
      vku_price: vku_course ? vku_course.min / 100 : null,
      exam_price_range: examPriceRange,
    },
  }
}

export type Prices = {
  lesson_price: number
  lesson_60_price: number
  lesson_75_price: number
  lesson_90_price: number
  packet_10_price: number
  packet_5_price: number
  packet_vku_price: number | null
  vku_price: number | null
  exam_price_range: { min: number; max: number } | null
}

export default function Component({
  validLocation,
  locationId,
  locationName,
  prices,
}:
  | {
      validLocation: false
      locationId: never
      locationName: never
      prices: never
    }
  | {
      validLocation: true
      locationId: number
      locationName: string
      prices: Prices
    }) {
  const [vkuSelected, setVkuSelected] = useState(false)

  if (!validLocation) {
    return null
  }

  return (
    <div className="mx-auto mt-20 max-w-container lg:mt-32">
      <Tabs defaultValue="deals" className="mt-6 lg:mt-10">
        <div className="mx-5 md:mx-6 md:flex md:items-center md:justify-between">
          <h2 className="text-4xl font-black lg:text-20xl">
            Deals & Preise in {locationName}
          </h2>
          <TabsList tabCount={2} className="mt-6 w-full md:mt-0 md:w-auto">
            <TabsTrigger value="deals">Deals</TabsTrigger>
            <TabsTrigger value="costs">Einzelpreise</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="deals" className="w-full">
          <Desktop
            vkuSelected={vkuSelected}
            setVkuSelected={setVkuSelected}
            prices={prices}
            locationId={locationId}
            locationName={locationName}
          />
          <Mobile
            vkuSelected={vkuSelected}
            setVkuSelected={setVkuSelected}
            prices={prices}
            locationId={locationId}
            locationName={locationName}
          />
          {/* SEO */}
          <div className="hidden">
            <DesktopCosts locationName={locationName} prices={prices} />
          </div>
        </TabsContent>
        <TabsContent value="costs" className="w-full">
          <DesktopCosts locationName={locationName} prices={prices} />
          <MobileCosts locationName={locationName} prices={prices} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

const Desktop = ({
  vkuSelected,
  setVkuSelected,
  prices,
  locationId,
  locationName,
}: {
  vkuSelected: boolean
  setVkuSelected: (vkuSelected: boolean) => void
  prices: Prices
  locationId: number
  locationName: string
}) => {
  return (
    <div className="mt-10 hidden px-6 lg:block">
      <div className="grid grid-cols-3">
        <div className="my-6 mr-5">
          <Packet5 prices={prices} locationName={locationName} />
        </div>
        <div className="-mx-5">
          <Packet10
            prices={prices}
            vkuSelected={vkuSelected}
            setVkuSelected={setVkuSelected}
            locationName={locationName}
          />
        </div>
        <div className="my-6 ml-5">
          <TrialLesson prices={prices} locationId={locationId} />
        </div>
      </div>
      <p className="mt-8 text-sm hyphens-manual">
        Ab der zweiten Fahrlektion kommen einmalig 90 CHF für die obligatorische
        Admin- & Versicherungs&shy;pauschale dazu.
      </p>
    </div>
  )
}

const Mobile = ({
  vkuSelected,
  setVkuSelected,
  prices,
  locationId,
  locationName,
}: {
  vkuSelected: boolean
  setVkuSelected: (vkuSelected: boolean) => void
  prices: Prices
  locationId: number
  locationName: string
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentIndex(emblaApi.selectedScrollSnap())
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <div className="mt-6 lg:hidden">
      <div className="w-full" ref={emblaRef}>
        <ul className="flex select-none">
          <li className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)]">
            <Packet10
              prices={prices}
              vkuSelected={vkuSelected}
              setVkuSelected={setVkuSelected}
              locationName={locationName}
            />
          </li>
          <li className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)]">
            <Packet5 prices={prices} locationName={locationName} />
          </li>
          <li className="mx-5 min-w-0 flex-[0_0_calc(100%-40px-20px)]">
            <TrialLesson prices={prices} locationId={locationId} />
          </li>
        </ul>
      </div>
      <p className="mx-5 mt-6 text-sm hyphens-manual">
        Ab der zweiten Fahrlektion kommen einmalig 90 CHF für die obligatorische
        Admin- & Versicherungs&shy;pauschale dazu.
      </p>
      <div className="mx-5 mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">{currentIndex + 1} von 3</p>
        <div className="space-x-3">
          <Button
            size="icon-small"
            icon={<CaretLeft weight="bold" />}
            onClick={prev}
            version="gray-outlined"
            disabled={prevDisabled}
          />
          <Button
            size="icon-small"
            icon={<CaretRight weight="bold" />}
            onClick={next}
            version="gray-outlined"
            disabled={nextDisabled}
          />
        </div>
      </div>
    </div>
  )
}

const Packet5 = ({
  prices,
  locationName,
}: {
  prices: Prices
  locationName: string
}) => {
  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 px-6 py-8 shadow-xs lg:rounded-r-none lg:border-r-0 lg:px-8 lg:pt-7.75 lg:pb-9">
      <div>
        <h2 className="text-lg font-bold lg:text-xl">5er Paket</h2>
        <p className="mt-2 text-gray-800">
          Für alle, die etwas weniger brauchen und trotzdem profitieren möchten.
        </p>
        <div className="mt-4 flex items-center space-x-2 text-gray-800">
          <Check size={24} weight="bold" className="text-gray-800" />
          <p>5 Fahrlektionen à 45 Minuten</p>
        </div>
      </div>
      <div>
        <div className="relative">
          <p className="text-7xl font-black lg:text-10xl">
            {prices.packet_5_price / 5} CHF
            <span className="ml-0.5 text-lg font-normal">/ Lektion</span>
          </p>
          <p className="absolute -top-6 left-0 text-lg font-bold lg:text-xl">
            statt{' '}
            <span className="relative inline-block">
              {prices.lesson_price} CHF
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 71 19"
                preserveAspectRatio="none"
                className="absolute top-[5.16px] right-[0.14px] bottom-[4.3px] left-[1.34px] hidden h-[calc(100%-9.46px)] w-[calc(100%-1.48px)] drop-shadow-xs lg:block"
              >
                <path
                  fill="#db2777"
                  d="M70.794 1.668c-1.19.205-2.348.408-3.536.618-21.18 3.72-42.133 8.59-62.543 15.154-1.136.361-2.267.726-3.402 1.097L0 14.233c1.159-.328 2.313-.652 3.473-.972C24.319 7.448 45.62 3.373 66.975.479A728 728 0 0 1 70.54 0z"
                />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 64 16"
                preserveAspectRatio="none"
                className="absolute top-[7.34px] right-[0.2px] bottom-[4.65px] left-[0.8px] h-[calc(100%-11.99px)] w-[calc(100%-1px)] drop-shadow-xs lg:hidden"
              >
                <path
                  fill="#db2777"
                  d="M64 1.273c-1.078.178-2.127.353-3.203.536C41.61 5.04 22.61 9.289 4.086 15.039Q2.542 15.512.998 16L0 12.715q1.571-.439 3.142-.866C22 6.673 41.262 3.033 60.581.432 61.665.284 62.723.142 63.808 0q.097.637.192 1.273"
                />
              </svg>
            </span>
          </p>
        </div>
        <p>Gesamtpreis: {prices.packet_5_price} CHF</p>
        <PacketDialog
          lessonCount={5}
          prices={prices}
          locationName={locationName}
        >
          <DialogTrigger asChild>
            <Button version="gray-outlined" className="mt-6 w-full">
              Jetzt anfragen
            </Button>
          </DialogTrigger>
        </PacketDialog>
      </div>
    </div>
  )
}

const Packet10 = ({
  vkuSelected,
  setVkuSelected,
  prices,
  locationName,
}: {
  vkuSelected: boolean
  setVkuSelected: (vkuSelected: boolean) => void
  prices: Prices
  locationName: string
}) => {
  const titleRef = useRef<HTMLParagraphElement>(null)
  const scopeRef = useRef<Scope>(null)

  useEffect(() => {
    scopeRef.current = createScope({ root: titleRef })

    return () => {
      if (scopeRef.current) {
        scopeRef.current.revert()
      }
    }
  }, [])

  useEffect(() => {
    if (!scopeRef.current) return

    const titleElement = titleRef.current
    if (!titleElement || !vkuSelected) return

    const vkuElement = titleElement.querySelector('.vku-text') as HTMLElement
    if (!vkuElement) return

    const { words } = splitText(vkuElement, {
      words: true,
    })

    animate(words, {
      translateY: [20, 0],
      opacity: [0, 1],
      duration: 200,
      delay: (_, i) => (i ? i * 20 : 0),
      easing: 'easeOutExpo',
    })
  }, [vkuSelected])

  return (
    <div className="rounded-xl bg-linear-to-b from-purple-600 to-purple-800 px-6 py-8 text-white shadow-xl lg:px-8 lg:pt-14 lg:pb-15.5">
      <h2 ref={titleRef} className="text-lg font-bold lg:text-xl">
        10er Paket{vkuSelected && <span className="vku-text"> + VKU</span>}
      </h2>
      <p className="mt-2">
        Für alle, die noch viel lernen, aber auch viel sparen wollen.
      </p>
      <div className="mt-4 flex items-center space-x-2">
        <Check size={24} weight="bold" className="text-white" />
        <p>10 Fahrlektionen à 45 Minuten</p>
      </div>
      <div className="mt-2 flex items-center space-x-2">
        <Check size={24} weight="bold" className="text-white" />
        <p>Optional mit VKU Kurs</p>
      </div>
      {prices.packet_vku_price && (
        <label
          className={clsx(
            'mt-6 flex items-center justify-between space-x-6 rounded-lg py-3 pr-3 pl-4 transition-colors',
            {
              'bg-purple-950 text-white': !vkuSelected,
              'bg-white text-gray-800': vkuSelected,
            }
          )}
        >
          <div>
            <p className="text-sm font-bold md:text-base">
              Buche den VKU dazu!
            </p>
            <p className="mt-0.5 text-sm md:text-base">
              Im Paket gibt’s den VKU für nur{' '}
              {prices.packet_vku_price - prices.packet_10_price} CHF.
            </p>
          </div>
          <Switch checked={vkuSelected} onCheckedChange={setVkuSelected} />
        </label>
      )}
      <div
        className={clsx('relative', {
          'mt-16': prices.packet_vku_price,
          'mt-20': !prices.packet_vku_price,
        })}
      >
        <p className="text-7xl font-black lg:text-10xl">
          {prices.packet_10_price / 10} CHF
          <span className="ml-0.5 text-lg font-normal">/ Lektion</span>
        </p>
        <p className="absolute -top-6 left-0 text-lg font-bold lg:text-xl">
          statt{' '}
          <span className="relative inline-block">
            {prices.lesson_price} CHF
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 71 19"
              preserveAspectRatio="none"
              className="absolute top-[5.16px] right-[0.14px] bottom-[4.3px] left-[1.34px] hidden h-[calc(100%-9.46px)] w-[calc(100%-1.48px)] drop-shadow-xs lg:block"
            >
              <path
                fill="#db2777"
                d="M70.794 1.668c-1.19.205-2.348.408-3.536.618-21.18 3.72-42.133 8.59-62.543 15.154-1.136.361-2.267.726-3.402 1.097L0 14.233c1.159-.328 2.313-.652 3.473-.972C24.319 7.448 45.62 3.373 66.975.479A728 728 0 0 1 70.54 0z"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 64 16"
              preserveAspectRatio="none"
              className="absolute top-[7.34px] right-[0.2px] bottom-[4.65px] left-[0.8px] h-[calc(100%-11.99px)] w-[calc(100%-1px)] drop-shadow-xs lg:hidden"
            >
              <path
                fill="#db2777"
                d="M64 1.273c-1.078.178-2.127.353-3.203.536C41.61 5.04 22.61 9.289 4.086 15.039Q2.542 15.512.998 16L0 12.715q1.571-.439 3.142-.866C22 6.673 41.262 3.033 60.581.432 61.665.284 62.723.142 63.808 0q.097.637.192 1.273"
              />
            </svg>
          </span>
        </p>
      </div>
      <p>
        Gesamtpreis:{' '}
        {vkuSelected ? prices.packet_vku_price : prices.packet_10_price} CHF
      </p>
      <PacketDialog
        lessonCount={10}
        vkuSelected={vkuSelected}
        prices={prices}
        locationName={locationName}
      >
        <DialogTrigger asChild>
          <Button version="white-gray" className="mt-6 w-full">
            Jetzt anfragen
          </Button>
        </DialogTrigger>
      </PacketDialog>
    </div>
  )
}

const TrialLesson = ({
  prices,
  locationId,
}: {
  prices: Prices
  locationId: number
}) => {
  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-gray-200 bg-gray-50 px-6 py-8 shadow-xs lg:rounded-l-none lg:border-l-0 lg:px-8 lg:pt-7.75 lg:pb-9">
      <div>
        <h2 className="text-lg font-bold lg:text-xl">Probelektion</h2>
        <p className="mt-2 text-gray-800">
          Für alle, die BLINK ganz unverbindlich testen.
        </p>
        <div className="mt-4 flex items-center space-x-2 text-gray-800">
          <Check size={24} weight="bold" />
          <p>1 Fahrlektionen à 45 Minuten</p>
        </div>
      </div>
      <div>
        <div className="relative">
          <p className="text-7xl font-black lg:text-10xl">
            59 CHF
            <span className="ml-0.5 text-lg font-normal">/ Lektion</span>
          </p>
          <p className="absolute -top-6 left-0 text-lg font-bold lg:text-xl">
            statt{' '}
            <span className="relative inline-block">
              {prices.lesson_price} CHF
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 71 19"
                preserveAspectRatio="none"
                className="absolute top-[5.16px] right-[0.14px] bottom-[4.3px] left-[1.34px] hidden h-[calc(100%-9.46px)] w-[calc(100%-1.48px)] drop-shadow-xs lg:block"
              >
                <path
                  fill="#db2777"
                  d="M70.794 1.668c-1.19.205-2.348.408-3.536.618-21.18 3.72-42.133 8.59-62.543 15.154-1.136.361-2.267.726-3.402 1.097L0 14.233c1.159-.328 2.313-.652 3.473-.972C24.319 7.448 45.62 3.373 66.975.479A728 728 0 0 1 70.54 0z"
                />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 64 16"
                preserveAspectRatio="none"
                className="absolute top-[7.34px] right-[0.2px] bottom-[4.65px] left-[0.8px] h-[calc(100%-11.99px)] w-[calc(100%-1px)] drop-shadow-xs lg:hidden"
              >
                <path
                  fill="#db2777"
                  d="M64 1.273c-1.078.178-2.127.353-3.203.536C41.61 5.04 22.61 9.289 4.086 15.039Q2.542 15.512.998 16L0 12.715q1.571-.439 3.142-.866C22 6.673 41.262 3.033 60.581.432 61.665.284 62.723.142 63.808 0q.097.637.192 1.273"
                />
              </svg>
            </span>
          </p>
        </div>
        <Link href={`/probelektion?location=${locationId}`}>
          <Button version="gray-outlined" className="mt-12 w-full">
            Probelektion online buchen
          </Button>
        </Link>
      </div>
    </div>
  )
}

type RequestForm = {
  first_name: string
  last_name: string
  phone: string
  email: string
  voucher: string
  interests: string[]
  driving_license_file: string
  location: string
  address: string
  utm_term: string | undefined
  utm_source: string | undefined
  utm_medium: string | undefined
  ref: string | undefined
}

const requestUrl = `${process.env.NEXT_PUBLIC_BLINK_ADMIN_URL}/api/website/v2/leads`

const PacketDialog = ({
  lessonCount,
  vkuSelected,
  prices,
  locationName,
  children,
}: {
  lessonCount: 5 | 10
  prices: Prices
  locationName: string
  children: ReactNode
} & (
  | {
      lessonCount: 10
      vkuSelected: boolean
    }
  | {
      lessonCount: 5
      vkuSelected?: never
    }
)) => {
  const [localVkuSelected, setLocalVkuSelected] = useState(vkuSelected || false)
  useEffect(() => {
    setLocalVkuSelected(vkuSelected || false)
  }, [vkuSelected])
  const { utmData } = useUtmCache()

  const [step, setStep] = useState<'cost_overview' | 'contact_data'>(
    'cost_overview'
  )
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const originalLessonPrice = lessonCount * prices.lesson_price
  const packetLessonPrice =
    lessonCount === 5 ? prices.packet_5_price : prices.packet_10_price
  const lessonSavings = originalLessonPrice - packetLessonPrice
  const vkuSavings =
    prices.vku_price && prices.packet_vku_price && localVkuSelected
      ? prices.vku_price - (prices.packet_vku_price - prices.packet_10_price)
      : 0
  const totalSavings = lessonSavings + vkuSavings
  const totalPrice =
    lessonCount === 5
      ? prices.packet_5_price
      : localVkuSelected
        ? prices.packet_vku_price
        : prices.packet_10_price

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useLaravelForm<RequestForm>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      voucher: '',
      address: '', // honeypot
      interests: [],
      driving_license_file: '',
      location: locationName ?? undefined,
      utm_term: utmData.utmTerm,
      utm_source: utmData.utmSource,
      utm_medium: utmData.utmMedium,
      ref: utmData.ref,
    },
  })

  const formValues = useWatch({
    control,
    defaultValue: {},
  })

  const [startedFillingOut, setStartedFillingOut] = useState(false)
  useEffect(() => {
    // check if a form property exists to ignore init of value
    if (formValues.first_name && !startedFillingOut) {
      setStartedFillingOut(true)
      trackEvent({
        event: 'packages_lead_started',
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues])

  const preparedData = (data: RequestForm) => {
    setLoading(true)
    const preparedData: any = {
      ...data,
    }
    let interests
    switch (lessonCount) {
      case 5:
        interests = [`5er-Paket für ${prices.packet_5_price} CHF`]
        break
      case 10:
        if (vkuSelected) {
          interests = [`10er-Paket + VKU für ${prices.packet_vku_price} CHF`]
        } else {
          interests = [`10er-Paket für ${prices.packet_10_price} CHF`]
        }
        break
    }
    preparedData.interests = interests
    return preparedData
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          setSubmitted(false)
          setStep('cost_overview')
          setLocalVkuSelected(vkuSelected || false)
          reset()
          setStartedFillingOut(false)
          trackEvent({
            event: 'packages_lead_opened',
          })
        }
      }}
    >
      {children}
      <DialogContent className="lg:max-w-160">
        {!submitted && (
          <>
            <DialogHeader title={`Anfrage für ein ${lessonCount}er Paket`} />
            <DialogBody>
              {step === 'cost_overview' && (
                <>
                  {prices.packet_vku_price && lessonCount === 10 && (
                    <div className="mb-6 rounded-xl border border-gray-150 bg-gray-50 p-5 shadow-xs">
                      <p className="text-lg font-black">
                        Verkehrskundeunterricht (VKU)
                      </p>
                      <p className="mt-1 text-gray-800">
                        Du musst noch den VKU machen? Buche ihn für nur{' '}
                        {prices.packet_vku_price - prices.packet_10_price} CHF
                        zu deinem Paket dazu!
                      </p>
                      <div className="mt-4 flex items-center space-x-2">
                        <Switch
                          id="vku-switch"
                          size={24}
                          checked={localVkuSelected}
                          onCheckedChange={setLocalVkuSelected}
                        />
                        <label htmlFor="vku-switch">Paket mit VKU buchen</label>
                      </div>
                    </div>
                  )}
                  <p className="text-xl font-black">Kostenübersicht</p>
                  <ul className="mt-3 space-y-3">
                    <li className="flex items-end justify-between">
                      <div className="flex items-center space-x-1">
                        <p>{lessonCount} Fahrlektionen à 45 Minuten</p>
                        <Popover>
                          <PopoverTrigger>
                            <Info
                              weight="fill"
                              size={20}
                              className="text-purple-600"
                            />
                          </PopoverTrigger>
                          <PopoverPortal>
                            <PopoverContent>
                              {lessonCount === 10 && (
                                <p>
                                  Eine Fahrlektion bei BLINK dauert
                                  standardmässig 45 Minuten. Es sind aber auch
                                  Lektionen à 60 Minuten, 75 Minuten oder 90
                                  Minuten möglich. Dein Paket beinhaltet 450
                                  Minuten (10 Lektionen) und du kannst damit die
                                  Dauer deiner Fahrlektionen so einteilen, wie
                                  du möchtest.
                                </p>
                              )}
                              {lessonCount === 5 && (
                                <p>
                                  Eine Fahrlektion bei BLINK dauert
                                  standardmässig 45 Minuten. Es sind aber auch
                                  Lektionen à 60 Minuten, 75 Minuten oder 90
                                  Minuten möglich. Dein Paket beinhaltet 225
                                  Minuten (5 Lektionen) und du kannst damit die
                                  Dauer deiner Fahrlektionen so einteilen, wie
                                  du möchtest.
                                </p>
                              )}
                            </PopoverContent>
                          </PopoverPortal>
                        </Popover>
                      </div>
                      <div className="text-right lg:flex lg:space-x-2">
                        <p className="text-xs line-through lg:text-base">
                          {originalLessonPrice} CHF
                        </p>
                        <p className="font-bold">{packetLessonPrice} CHF</p>
                      </div>
                    </li>
                    {prices.packet_vku_price && localVkuSelected && (
                      <li className="flex items-end justify-between">
                        <p>VKU Kurs</p>
                        <div className="text-right lg:flex lg:space-x-2">
                          <p className="text-xs line-through lg:text-base">
                            {prices.vku_price} CHF
                          </p>
                          <p className="font-bold">
                            {prices.packet_vku_price - prices.packet_10_price}{' '}
                            CHF
                          </p>
                        </div>
                      </li>
                    )}
                  </ul>
                  <div className="mt-3 flex items-center justify-between border-t border-gray-150 pt-3">
                    <p>Total</p>
                    <div className="flex items-center space-x-2">
                      <Tag color="pink" text={`${totalSavings} CHF sparen`} />
                      <p className="font-bold">{totalPrice} CHF</p>
                    </div>
                  </div>
                  <div className="mt-8 space-y-3 lg:mt-12 lg:flex lg:justify-end lg:space-y-0">
                    <Button
                      className="w-full lg:ml-4 lg:w-auto"
                      onClick={() => setStep('contact_data')}
                    >
                      Weiter
                    </Button>
                    <DialogClose asChild>
                      <Button
                        version="white-gray"
                        className="w-full lg:-order-1 lg:w-auto"
                      >
                        Abbrechen
                      </Button>
                    </DialogClose>
                  </div>
                </>
              )}
              {step === 'contact_data' && (
                <>
                  <button
                    onClick={() => setStep('cost_overview')}
                    className="flex space-x-2 text-sm text-purple-600"
                  >
                    <CaretLeft size={20} weight="bold" />
                    <span className="font-bold">Zurück</span>
                  </button>
                  <div className="mt-8 rounded-lg bg-purple-600 p-5 text-white">
                    <p className="font-bold">Du bist schon bei BLINK? 👀</p>
                    <p className="mt-1">
                      Buche dein Paket direkt im myBLINK, da geht’s schneller!
                    </p>
                    <Button
                      as={Link}
                      href="https://myblink.blinkdrive.ch/appointments"
                      target="_blank"
                      version="white-gray"
                      size="small"
                      className="mt-4"
                    >
                      Im myBLINK buchen
                    </Button>
                  </div>
                  <form
                    onSubmit={handleSubmit(
                      () => {
                        trackEvent({
                          event: 'packages_lead_sent',
                          params: {
                            value: 350,
                          },
                        })
                        setSubmitted(true)
                        reset()
                        setLoading(false)
                      },
                      preparedData,
                      () => setLoading(false)
                    )}
                    method="POST"
                    action={requestUrl}
                  >
                    <p className="mt-8 text-xl font-black">Kontakt</p>
                    <p className="mt-3 text-gray-800">
                      Fülle das Formular aus und wir nehmen mit dir Kontakt auf.
                    </p>
                    <div className="mt-6 grid gap-3 md:grid-cols-2">
                      <Input
                        type="first_name"
                        label="Vorname"
                        required
                        {...register('first_name')}
                      />
                      <Input
                        type="last_name"
                        label="Nachname"
                        required
                        {...register('last_name')}
                      />
                      <Input
                        type="email"
                        label="E-Mail"
                        required
                        {...register('email')}
                      />
                      <Input
                        type="phone"
                        label="Handynummer"
                        required
                        {...register('phone')}
                      />
                      <Input
                        type="voucher"
                        label="Gutscheincode (optional)"
                        {...register('voucher')}
                      />
                    </div>
                    <p className="mt-6 font-bold">Upload Lernfahrausweis</p>
                    <p className="mt-2">
                      Damit wir dich später an die praktische Prüfung anmelden
                      können, benötigen wir Informationen aus deinem
                      Lernfahrausweis. Fotografiere bitte die Innenseite deines
                      Dokuments und lade es gleich hier hoch.
                    </p>
                    <div className="mt-4">
                      <Controller
                        control={control}
                        name="driving_license_file"
                        render={({ field: { value, onChange } }) => (
                          <FileUpload
                            value={value}
                            onChange={onChange}
                            adminGetFile={adminGetFile}
                            adminPostFile={adminPostFile}
                            adminDeleteFile={adminDeleteFile}
                          >
                            {(fileUploadChildrenProps) => (
                              <ImageUploadMask
                                {...fileUploadChildrenProps}
                                emptyText={() => 'Foto aufnehmen und hochladen'}
                                imageAlt="Lernfahrausweis Kat. B"
                              />
                            )}
                          </FileUpload>
                        )}
                      />
                      <ErrorMessage>
                        {errors.driving_license_file?.message as string}
                      </ErrorMessage>
                    </div>
                    <div className="mt-8 space-y-3 lg:mt-12 lg:flex lg:justify-end lg:space-y-0">
                      <Button
                        type="submit"
                        loading={loading}
                        className="w-full lg:ml-4 lg:w-auto"
                      >
                        Unverbindlich anfragen
                      </Button>
                      <DialogClose asChild>
                        <Button
                          type="button"
                          version="white-gray"
                          className="w-full lg:-order-1 lg:w-auto"
                        >
                          Abbrechen
                        </Button>
                      </DialogClose>
                    </div>
                  </form>
                </>
              )}
            </DialogBody>
          </>
        )}
        {submitted && (
          <DialogSimple>
            <p className="text-xl font-black">Vielen Dank!</p>
            <p className="mt-3">
              Wir haben deine Anfrage erhalten und melden uns schnellstmöglich
              bei dir.
            </p>
            <div className="mt-8 flex justify-end">
              <DialogClose asChild>
                <Button type="button" className="w-full lg:w-auto">
                  Schliessen
                </Button>
              </DialogClose>
            </div>
          </DialogSimple>
        )}
      </DialogContent>
    </Dialog>
  )
}
